import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/24/outline";
import { Box, Button, Group, rem } from "@mantine/core";
import { DateInput, YearPickerInput } from "@mantine/dates";
import { useState } from "react";
import {
  dateInputStyles,
  getDateInputProps,
  nextIconElement,
  previousIconElement,
} from "../../styles/dateInputStyles";

interface MonthYearDateInputProps {
  value: Date | null;
  onChange: (value: Date | null) => void;
  label: string;
  required?: boolean;
  placeholder?: string;
}

export function MonthYearDateInput({
  value,
  onChange,
  label,
  required = false,
  placeholder = "MM/YYYY",
}: MonthYearDateInputProps) {
  // Get props from our shared style file
  const dateInputBaseProps = getDateInputProps();
  const [yearPickerOpen, setYearPickerOpen] = useState(false);

  // Function to quickly navigate years
  const navigateYears = (years: number) => {
    if (!value) {
      // If no date is selected, use current date as base
      const newDate = new Date();
      newDate.setFullYear(newDate.getFullYear() + years);
      onChange(newDate);
    } else {
      // Add years to current date
      const newDate = new Date(value);
      newDate.setFullYear(newDate.getFullYear() + years);
      onChange(newDate);
    }
  };

  return (
    <Box>
      {/* Main date input */}
      <DateInput
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        valueFormat="MM/YYYY"
        required={required}
        styles={dateInputStyles}
        nextIcon={<span style={{ fontSize: "16px" }}>{nextIconElement()}</span>}
        previousIcon={
          <span style={{ fontSize: "16px" }}>{previousIconElement()}</span>
        }
        defaultLevel="month"
        maxLevel="month"
        onClick={() => setYearPickerOpen(false)}
        {...dateInputBaseProps}
      />

      {/* Year navigation controls */}
      <Group mt={4} gap="xs" justify="flex-end">
        <Button
          variant="subtle"
          size="compact-xs"
          onClick={() => navigateYears(-10)}
          leftSection={
            <ChevronDoubleLeftIcon
              style={{ width: rem(14), height: rem(14) }}
            />
          }
        >
          -10y
        </Button>
        <Button
          variant="subtle"
          size="compact-xs"
          onClick={() => navigateYears(-1)}
        >
          -1y
        </Button>
        <Button
          variant="subtle"
          size="compact-xs"
          onClick={() => setYearPickerOpen(!yearPickerOpen)}
        >
          Year
        </Button>
        <Button
          variant="subtle"
          size="compact-xs"
          onClick={() => navigateYears(1)}
        >
          +1y
        </Button>
        <Button
          variant="subtle"
          size="compact-xs"
          onClick={() => navigateYears(10)}
          rightSection={
            <ChevronDoubleRightIcon
              style={{ width: rem(14), height: rem(14) }}
            />
          }
        >
          +10y
        </Button>
      </Group>

      {/* Year picker that shows only when user wants it */}
      {yearPickerOpen && (
        <Box mt={4}>
          <YearPickerInput
            value={value}
            onChange={(date) => {
              onChange(date);
              setYearPickerOpen(false);
            }}
            placeholder="Select year"
            styles={dateInputStyles}
            allowDeselect
            {...dateInputBaseProps}
          />
        </Box>
      )}
    </Box>
  );
}
